import { forwardRef, useCallback, useState } from "react";
import { PasswordConfirm } from "../PasswordConfirm";
import { useAccountData } from "../../../Hooks";
import { registerTransactionalPassword } from "../../../Hooks/UsePix";

export const PixTransferPasswordStep = forwardRef(function PixTransferPasswordStep(
  {
    onSubmit,
    onBack,
    error,
    loading,
    onDismissError,
    hideFooter = false,
    stepUpOptions,
    /**
     * From parent /me: true | false | undefined.
     * undefined = still loading — keep pair keypad (confirm), never flash full register pad.
     */
    transactionalPasswordQualified,
  },
  ref
) {
  const { state: accountData, handleGET: refreshAccount } = useAccountData();
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState(null);
  const [justRegistered, setJustRegistered] = useState(false);

  const qualifiedFromParent = transactionalPasswordQualified === true;
  const unqualifiedFromParent = transactionalPasswordQualified === false;
  const qualifiedFromAccount = accountData?.transactionalPasswordQualified === true;
  const accountReady = Boolean(accountData?.userId);

  const isQualified =
    justRegistered || qualifiedFromParent || qualifiedFromAccount;

  // Only switch to full register keypad when we *know* the user is not qualified.
  // While /me is unknown, keep the pair keypad (previous UX) to avoid transition flicker.
  const needsRegister =
    !isQualified
    && (unqualifiedFromParent || (accountReady && !qualifiedFromAccount));

  const handleSubmit = useCallback(
    async (payload) => {
      if (needsRegister) {
        const { password, confirmPassword } = payload || {};
        setRegistering(true);
        setRegisterError(null);
        onDismissError?.();

        const result = await registerTransactionalPassword({
          password,
          confirmPassword,
        });

        setRegistering(false);

        if (!result.success) {
          setRegisterError({
            title: result.message || "Aviso",
            description: result.description,
          });
          return;
        }

        setJustRegistered(true);
        refreshAccount?.({});
        return;
      }

      onSubmit(payload);
    },
    [needsRegister, onSubmit, onDismissError, refreshAccount]
  );

  const isSending = Boolean(loading) && !needsRegister;

  return (
    <PasswordConfirm
      ref={ref}
      variant="inline"
      mode={needsRegister ? "register" : "confirm"}
      submitPassword={handleSubmit}
      onBack={onBack}
      error={registerError || error}
      loading={loading || registering}
      onDismissError={() => {
        setRegisterError(null);
        onDismissError?.();
      }}
      hideFooter={hideFooter}
      stepUpOptions={stepUpOptions}
      title={isSending ? "Enviando Pix…" : null}
      subtitle={
        isSending
          ? "Aguarde enquanto confirmamos a transferência. Não feche esta tela."
          : null
      }
    />
  );
});
